import React, { useState, useEffect } from 'react';
import {
  Page,
  Popup,
  Navbar,
  NavRight,
  Link,
  Block,
  List,
  Button,
  ListItem,
  Checkbox
} from 'framework7-react';
import II18 from '../translate/II18';
import { gae } from '../helpers/Analytics';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import Spinner from '../helpers/Spinner';
import PinCodeBlock from '../common/PinCodeBlock';
import { getIcon } from '../helpers/utils';
import PaperflyAPI from '../../server/api/PaperflyAPI';
import Checkmark from '../helpers/Checkmark';
import { setOtpRequestId } from '../../redux/actions/rootAction';

const AES_STEPS = {
  ACCEPT_AGB: 'ACCEPT_AGB',
  CHECK_CODE: 'CHECK_CODE',
  SUCCESS: 'SUCCESS'
};
export default function AdvancedSignaturePopUp(props) {
  const {
    opened,
    onClose,
    noanimate,
    onDone,
    aesOptMode,
    requestedDocuments
  } = props;

  const aesDocuments = requestedDocuments.filter(
    d => d.curentRecipientAdvancedSignaturesList?.length
  );

  const companyEmail = useSelector(state => state.companyEmail);
  const recipientName = useSelector(state => state.recipientName);
  const recipientEmail = useSelector(state => state.recipientEmail);
  const recipientPhone = useSelector(state => state.recipientPhone);
  const recipientRequestId = useSelector(state => state.recipientRequestId);
  const otpRequestId = useSelector(state => state.otpRequestId);

  const dispatch = useDispatch();

  const [showPinIsWrongMessage, setShowPinIsWrongMessage] = useState(false);
  const [agbAccepted, setAgbAccepted] = useState(false);
  const [step, setStep] = useState(AES_STEPS.ACCEPT_AGB);
  const [code, setCode] = useState('');
  const [lastError, setLastError] = useState('');
  const [waitingForRequest, setWaitingForRequest] = useState(false);

  const handleCancel = () => {
    gae('AES_REGISTER_POP_UP', 'CLOSE');
    onClose();
  };

  const handleDone = () => {
    onDone();
  };

  const goToCheckCodeStep = () => {
    optRequestVerification();
  };

  const goToSuccessStep = () => {
    setStep(AES_STEPS.SUCCESS);
    setTimeout(handleDone, 3000);
  };

  const scrollTo = id => {
    if (!window.isDesktop) {
      const input = document.getElementById(id);

      input.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  useEffect(() => {
    if (step === AES_STEPS.CHECK_CODE) {
      scrollTo('btnAesSign');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  useEffect(() => {
    const element = document.getElementById('aes_agb');
    element.tabIndex = 0;
    element.onkeydown = event => {
      if (event.keyCode === 32) {
        event.preventDefault();
        element.click();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (agbAccepted && step === AES_STEPS.ACCEPT_AGB) {
      scrollTo('btnRequestCode');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agbAccepted]);

  const onAcceptAgbChange = e => {
    setAgbAccepted(e.target.checked);
  };

  const toggleAgb = () => {
    setAgbAccepted(!agbAccepted);
  };

  const verifyCodeChange = code => {
    setShowPinIsWrongMessage(false);
    setCode(code);
  };

  const errorOnRequestVerification = error => {
    gae('AES_REGISTER_POP_UP', 'ERROR_ON_REQUEST_VERIFY', error);
    setLastError(error);
  };

  const optRequestVerification = () => {
    setLastError('');
    setWaitingForRequest(true);

    gae('AES_REGISTER_POP_UP', 'REQUEST_OTP_VERIFICATION');
    PaperflyAPI.otpRequestVerification(recipientRequestId).then(
      response => {
        setWaitingForRequest(false);

        if (response.errorCode) {
          errorOnRequestVerification(response.errorCode);
        } else {
          gae('AES_REGISTER_POP_UP', 'SET_OTP_REQUEST_ID');
          dispatch(setOtpRequestId(response.otpRequestId));
          setStep(AES_STEPS.CHECK_CODE);
        }
      },
      error => {
        setWaitingForRequest(false);
        errorOnRequestVerification(
          (error.response &&
            (error.response.data.error || error.response.data.errorCode)) ||
            error.message
        );
      }
    );
  };

  const errorOnCodeVerify = error => {
    gae('AES_REGISTER_POP_UP', 'ERROR_ON_VERIFY', error);
    setShowPinIsWrongMessage(true);
  };

  const aesVerifyCode = () => {
    setLastError('');
    setWaitingForRequest(true);
    setShowPinIsWrongMessage(false);

    gae('AES_REGISTER_POP_UP', 'VERIFY_CODE');
    PaperflyAPI.otpVerifyCode(otpRequestId, code).then(
      response => {
        setWaitingForRequest(false);

        if (response.errorCode || response.verificationStatus !== 'VALID') {
          errorOnCodeVerify(response.errorCode || response.verificationStatus);
        } else {
          gae('AES_REGISTER_POP_UP', 'CODE_VERIFIED_SUCCESSFULLY');

          goToSuccessStep();
        }
      },
      error => {
        setWaitingForRequest(false);
        errorOnCodeVerify(
          error.response.data.error || error.response.data.errorCode
        );
      }
    );
  };

  return (
    <Popup
      id={'aes_register_popup'}
      className="aes_register_popup popup_scroll"
      animate={!noanimate}
      opened={Boolean(opened)}
      swipeToClose={false}
      closeByBackdropClick={false}
      closeByOutsideClick={false}
      onPopupOpen={() => gae('AES_REGISTER_POP_UP', 'SHOW')}
    >
      <Page
        style={{
          background: '#fff',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between'
        }}
        className={'page-content-full-height'}
      >
        <Navbar title={II18.get('dialog_aes_popup_title')}>
          <NavRight>
            <Link onClick={handleCancel}>
              <II18>title_close</II18>
            </Link>
          </NavRight>
        </Navbar>

        <div
          style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}
        >
          <div
            style={{
              minHeight: 170,
              padding: '20px 20px 30px',
              overflow: 'auto'
            }}
          >
            {waitingForRequest && <Spinner fullScreen />}

            {aesOptMode && requestedDocuments.length > 1 && (
              <>
                <label>{II18.get('aes_popup_docs_title')}</label>
                <ul style={{ marginTop: 10, marginBottom: 20 }}>
                  {aesDocuments.map(doc => (
                    <li key={doc.id}>{doc.name}</li>
                  ))}
                </ul>
              </>
            )}

            {aesOptMode && (
              <label>
                <II18>aes_popup__info</II18>
              </label>
            )}

            {lastError && (
              <div className="alert alert-danger" role="alert">
                <II18>opt_popup_error</II18>
              </div>
            )}

            <List
              className={'tinnyList ' + (!window.isDesktop ? ' two-lines' : '')}
              mediaList
              noHairlinesBetween
              noHairlines
              style={{ marginTop: 10, marginBottom: 10 }}
            >
              <ListItem>
                {getIcon('material:person', 20, 'media')}
                <div slot={'title'}>
                  <span className={'aes_user_info_title'}>
                    {II18.get('aes_popup_label_name')}:
                  </span>
                  <span className={'aes_user_info_value'}>{recipientName}</span>
                </div>
              </ListItem>

              <ListItem>
                {getIcon('material:email', 20, 'media')}
                <div slot={'title'}>
                  <span className={'aes_user_info_title'}>
                    {II18.get('aes_popup_label_emailAddress')}:
                  </span>
                  <span className={'aes_user_info_value'}>
                    {recipientEmail}
                  </span>
                </div>
              </ListItem>

              <ListItem>
                {getIcon('material:phone', 20, 'media')}
                <div slot={'title'}>
                  <span className={'aes_user_info_title'}>
                    {II18.get('aes_popup_label_phone')}:
                  </span>
                  <span className={'aes_user_info_value'}>
                    {recipientPhone}
                  </span>
                </div>
              </ListItem>

              <ListItem>
                {getIcon('material:info', 20, 'media')}
                <div slot={'title'}>
                  <span className={'aes_user_info_title'}>REQUEST ID:</span>
                  <span
                    className={'aes_user_info_value'}
                    style={{ fontSize: window.isdesktop ? 14 : 12 }}
                  >
                    {recipientRequestId}
                  </span>
                </div>
              </ListItem>
            </List>

            {step !== AES_STEPS.SUCCESS && (
              <>
                <p>
                  {II18.get('aes_popup_if_data_wrong')}
                  <Link
                    target={'_blank'}
                    external
                    href={'mailto: ' + companyEmail}
                  >
                    {II18.get('aes_popup_if_data_wrong_absender')}
                  </Link>
                </p>

                <p
                  style={{
                    padding: 10,
                    paddingBottom: 0,
                    marginBottom: 0
                  }}
                >
                  <Checkbox
                    tabIndex={0}
                    onChange={e => onAcceptAgbChange(e)}
                    checked={agbAccepted}
                    name="aes_agb"
                    id="aes_agb"
                  />
                  <label htmlFor={'aes_agb'} onClick={toggleAgb}>
                    {' '}
                    {II18.get('aes_popup_label_agb')}
                  </label>
                </p>
              </>
            )}

            {step === AES_STEPS.ACCEPT_AGB && (
              <Block style={{ display: 'flex', placeContent: 'center' }}>
                <Button
                  id={'btnRequestCode'}
                  disabled={!agbAccepted}
                  onClick={goToCheckCodeStep}
                  raised
                  fill
                  large
                >
                  {II18.get('opt_get_code')}
                </Button>
              </Block>
            )}

            {/*<input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            style={{ height: 20 }}
          />*/}

            {step === AES_STEPS.CHECK_CODE && (
              <>
                <PinCodeBlock
                  noLines
                  fieldsCount={6}
                  title={'opt_enter_code'}
                  topMargin={0}
                  verifyCodeChange={verifyCodeChange}
                  showPinIsWrongMessage={showPinIsWrongMessage}
                  titleFontSize={15}
                  oneTimeCode
                />

                <Block style={{ display: 'flex', placeContent: 'center' }}>
                  <Button
                    id={'btnAesSign'}
                    onClick={aesVerifyCode}
                    raised
                    large
                    fill
                    disabled={code.length < 6 || !agbAccepted}
                  >
                    {II18.get(aesOptMode ? 'aes_popup_sign' : 'title_confirm')}
                  </Button>
                </Block>
              </>
            )}

            {step === AES_STEPS.SUCCESS && (
              <Block>
                <p
                  style={{
                    textAlign: 'center',
                    fontSize: 16,
                    marginTop: '7vh',
                    marginBottom: '5vh'
                  }}
                >
                  <II18>opt_code_success</II18>
                </p>
                <Checkmark />
              </Block>
            )}
          </div>

          <div className={'desktop-popup-footer'}>
            <div className={'buttons'}>
              <Button
                onClick={() =>
                  step === AES_STEPS.SUCCESS ? handleDone() : handleCancel()
                }
              >
                {II18.get(
                  step === AES_STEPS.SUCCESS ? 'title_done' : 'title_cancel'
                )}
              </Button>
            </div>
          </div>
        </div>
      </Page>
    </Popup>
  );
}

AdvancedSignaturePopUp.propTypes = {
  aesOptMode: PropTypes.bool,
  noanimate: PropTypes.bool,
  opened: PropTypes.any,
  onClose: PropTypes.func,
  onDone: PropTypes.func,
  requestedDocuments: PropTypes.array.isRequired
};
